import type { FormValues, PhotoItem } from '@/types'
import type {
  ClaimDraftPayload,
  ClaimGetResponse,
  ClaimSectionImage,
  ClaimVehicle,
  ClaimVehicleBody,
} from '@/types/claim'
import { normaliseToKey } from '@/data/refCategories'
import { REF_KEYS } from '@/data/refKeys'
import { SECTIONS } from '@/data/sections.data'
import type { AttributeDefinition } from '@/services/attributeService'

/** category key (e.g. TRUCK_BODY_<USE>) → attribute definitions for that body config */
export type AttrCache = Record<string, AttributeDefinition[]>

/**
 * Resolves the ref-master category to use for a Body Information field.
 * Prefers the attribute's own `ref_category` (shared/common dropdown values)
 * when present, falling back to the vehicle-specific derived key.
 */
function resolveBodyFieldCategory(
  fieldId: string,
  derivedCategory: string | null,
  attrDefs: AttributeDefinition[] | undefined
): string | null {
  const refCategory = attrDefs?.find((a) => a.attribute_name === fieldId)?.ref_category
  return refCategory || derivedCategory
}

// Build a lookup map from section id (e.g. 'standard-equipment') → section name (e.g. 'Standard Equipment')
const SECTION_ID_TO_NAME: Record<string, string> = Object.fromEntries(
  SECTIONS.map((s) => [s.id, s.name])
)

// ── Helpers ───────────────────────────────────────────────────

export type RefIdLookup = (category: string, label: string) => number | undefined

const PLACEHOLDER = new Set(['', 'Select'])

function toInt(value: string | undefined): number {
  const n = parseInt(value ?? '', 10)
  return isNaN(n) ? 0 : n
}

function toOptionalInt(value: string | undefined): number | null {
  if (!value) return null
  const n = parseInt(value, 10)
  return isNaN(n) ? null : n
}

/**
 * Resolves a form value (label string) to its ref-master numeric ID.
 * When getRefId is available, it is authoritative — if the value has no matching
 * label in the ref store the field is treated as empty (null) rather than falling
 * back to parseInt, which would forward a stale or AI-hallucinated numeric string
 * as a ref ID and cause a DB insertion error.
 * The parseInt path is only reached when no ref-store lookup is available at all.
 */
function toRefId(
  value: string,
  category: string,
  getRefId: RefIdLookup | undefined
): number | null {
  if (!value || PLACEHOLDER.has(value)) return null
  if (getRefId) {
    const id = getRefId(category, value)
    return id !== undefined ? id : null
  }
  const n = parseInt(value, 10)
  return isNaN(n) || n === 0 ? null : n
}

function toBool(value: string | null): boolean {
  return value === 'true' || value === '1'
}

function toStr(value: string | boolean | number | null): string {
  if (value === undefined || value === null) return ''
  return String(value)
}

function field(formValues: FormValues, section: string, fieldId: string): string {
  return formValues[section]?.[fieldId] ?? ''
}

// ── formValues → ClaimDraftPayload ────────────────────────────

export function formValuesToClaimPayload(formValues: FormValues, getRefId?: RefIdLookup, photos?: PhotoItem[], attrCache?: AttrCache): ClaimDraftPayload {
  const basic = (id: string) => field(formValues, 'basic-information', id)
  const standard = (id: string) => field(formValues, 'standard-equipment', id)
  const additional = (id: string) => field(formValues, 'additional-options', id)
  const condition = (id: string) => field(formValues, 'vehicle-condition', id)

  const vehicleTypeLabel = basic('vehicleType')
  const vehicleTypeId = toRefId(basic('vehicleType'), REF_KEYS.VEHICLE_TYPE, getRefId)
  const vehicleKind = vehicleTypeLabel === 'Truck/Tractor' ? 'truck' as const
    : vehicleTypeLabel === 'Trailer' ? 'trailer' as const
    : '' as const

  // Build body array from body-information section
  const body: ClaimVehicleBody[] = buildBodyPayload(formValues, getRefId, attrCache)

  // Build section images array from uploaded photos
  const sectionImages: ClaimSectionImage[] = buildSectionImagesPayload(photos ?? [], getRefId)

  const makeLabel = vehicleKind !== '' ? standard('make') : ''
  const makeCategory = vehicleKind === 'truck' ? REF_KEYS.TRUCK_MAKE
    : vehicleKind === 'trailer' ? REF_KEYS.TRAILER_MAKE : ''
  const makeIsNotInList = makeLabel.toLowerCase() === 'not in list'
  const modelCategory = makeIsNotInList
    ? REF_KEYS.NOT_IN_LIST_MODEL
    : vehicleKind === 'truck'
      ? `TRUCK_MODEL_${normaliseToKey(makeLabel)}`
      : vehicleKind === 'trailer' ? `TRAILER_MODEL_${normaliseToKey(makeLabel)}` : ''

  const transMfgLabel = vehicleKind === 'truck' ? standard('transMfg') : ''
  const transMfgIsNotInList = transMfgLabel.toLowerCase() === 'not in list'
  const transSpeedCategory = transMfgIsNotInList
    ? REF_KEYS.NOT_IN_LIST_MODEL
    : transMfgLabel && !PLACEHOLDER.has(transMfgLabel)
      ? `TRUCK_TRANS_SPEED_${normaliseToKey(transMfgLabel)}`
      : ''

  const vehicle: ClaimVehicle = {
    kind: vehicleKind,
    vin: standard('vin'),
    year: toOptionalInt(standard('year')),
    makeId: toRefId(makeLabel, makeCategory, getRefId),
    modelId: toRefId(standard('model'), modelCategory, getRefId),
    numAxles: toOptionalInt(standard('noOfAxles')),
    brandedSalvageTitle: toBool(standard('branded')),
    osWheelTypeId: vehicleKind === 'truck'
      ? toRefId(additional('osRearWheelType'), REF_KEYS.TRUCK_WHEEL_TYPE, getRefId)
      : vehicleKind === 'trailer'
        ? toRefId(additional('osWheelType'), REF_KEYS.TRAILER_OS_WHEEL_TYPE, getRefId)
        : null,
    isWheelTypeId: vehicleKind === 'truck'
      ? toRefId(additional('isRearWheelType'), REF_KEYS.TRUCK_WHEEL_TYPE, getRefId)
      : vehicleKind === 'trailer'
        ? toRefId(additional('isWheelType'), REF_KEYS.TRAILER_IS_WHEEL_TYPE, getRefId)
        : null,
    condBodyId: vehicleKind === 'truck'
      ? toRefId(condition('body'), REF_KEYS.TRUCK_VEHICLE_CONDITON, getRefId)
      : vehicleKind === 'trailer'
        ? toRefId(condition('exteriorBody'), REF_KEYS.TRAILER_VEHICLE_CONDITON, getRefId)
        : null,
    condPaintId: vehicleKind === 'truck'
      ? toRefId(condition('interiorPaint'), REF_KEYS.TRUCK_VEHICLE_CONDITON, getRefId)
      : vehicleKind === 'trailer'
        ? toRefId(condition('exteriorPaint'), REF_KEYS.TRAILER_VEHICLE_CONDITON, getRefId)
        : null,
    frontTireWearPct: toOptionalInt(condition('tiresFWearRmg')),
    rearTireWearPct: toOptionalInt(condition('tiresRWearRmg')),
    notes: condition('conditional_notes'),
    truck: vehicleKind === 'truck'
      ? {
          useTypeId: toRefId(standard('use'), REF_KEYS.TRUCK_USE, getRefId) ?? null,
          useSubtypeId: standard('useSubtype') && !PLACEHOLDER.has(standard('use'))
            ? toRefId(standard('useSubtype'), `TRUCK_USE_SUBTYPE_${normaliseToKey(standard('use'))}`, getRefId) ?? null
            : null,
          gvwrId: toRefId(standard('gvw'), REF_KEYS.TRUCK_GVWR, getRefId) ?? null,
          styleId: toRefId(standard('style'), REF_KEYS.TRUCK_STYLE, getRefId) ?? null,
          typeId: toRefId(standard('truck_type'), REF_KEYS.TRUCK_TYPE, getRefId) ?? null,
          engineMfg: toRefId(standard('engineMfg'), REF_KEYS.TRUCK_ENGINE_MFG, getRefId) ?? null,
          engineModel: standard('engineModel') || null,
          engineHp: toRefId(standard('engineHP'), REF_KEYS.TRUCK_ENGINE_HP, getRefId) ?? null,
          transMfg: toRefId(standard('transMfg'), REF_KEYS.TRUCK_TRANS_MFG, getRefId) ?? null,
          transModel: standard('transModel') || null,
          numSpeeds: toRefId(standard('noOfSpeeds'), transSpeedCategory, getRefId) ?? null,
          numDriveAxles: toOptionalInt(standard('noOfDriveAxles')),
          numTagAxles: toOptionalInt(standard('noOfTagAxles')),
          frontAxleRatingLbs: toRefId(standard('frontAxleRating'), REF_KEYS.TRUCK_FRONT_AXLE_RATING, getRefId) ?? null,
          rearAxleRatingLbs: toRefId(standard('rearAxleRating'), REF_KEYS.TRUCK_REAR_AXLE_RATING, getRefId) ?? null,
          odometer: toInt(standard('odometer')),
          sleeperConvSizeId: toRefId(additional('convSleeperSize'), REF_KEYS.TRUCK_SLEEPER_SIZE, getRefId) ?? null,
          sleeperCoeSizeId: toRefId(additional('coeSize'), REF_KEYS.TRUCK_COE_SIZE, getRefId) ?? null,
          apuId: toRefId(additional('apu'), REF_KEYS.TRUCK_APU, getRefId) ?? null,
          trimLevelId: toRefId(additional('trimLevel'), REF_KEYS.TRUCK_TRIM_LEVEL, getRefId) ?? null,
          suspensionId: toRefId(additional('suspension'), REF_KEYS.TRUCK_SUSPENSION, getRefId) ?? null,
          fifthWheelTypeId: toRefId(additional('fifthWheelType'), REF_KEYS.TRUCk_FIFTH_WHEEL_TYPE, getRefId) ?? null,
          hitchTypeId: toRefId(additional('hitchType'), REF_KEYS.TRUCK_HITCH_TYPE, getRefId) ?? null,
          frontWheelTypeId: toRefId(additional('frontWheelType'), REF_KEYS.TRUCK_WHEEL_TYPE, getRefId) ?? null,
          frontTireSize: additional('frontTireSize') || null,
          rearTireSize: additional('rearTireSize') || null,
          liftGateCapacity: toRefId(additional('liftGateCapacity'), REF_KEYS.TRUCK_LIFT_GATE_CAPACITY, getRefId) ?? null,
          gps: toBool(additional('gps')),
          airRideCab: toBool(additional('airRideCab')),
          airConditioning: toBool(additional('airConditioning')),
          pto: toBool(additional('pto')),
          wetLineKit: toBool(additional('wetLineKit')),
          airBrakes: toBool(additional('airBrakes')),
          engineBrake: toBool(additional('engineBrake')),
          sideFairing: toBool(additional('sideFairing')),
          windDeflector: toBool(additional('windDeflector')),
          tankFairings: toBool(additional('tankFairings')),
          fullAerodynamics: toBool(additional('fullAerodynamics')),
          condInteriorId: toRefId(condition('interior'), REF_KEYS.TRUCK_VEHICLE_CONDITON, getRefId) ?? null,
          condGlassId: toRefId(condition('interiorGlass'), REF_KEYS.TRUCK_VEHICLE_CONDITON, getRefId) ?? null,
          condEngineId: toRefId(condition('drivetrainEngine'), REF_KEYS.TRUCK_VEHICLE_CONDITON, getRefId) ?? null,
          condTransId: toRefId(condition('drivetrainTransmission'), REF_KEYS.TRUCK_VEHICLE_CONDITON, getRefId) ?? null,
        }
      : null,
    trailer: vehicleKind === 'trailer'
      ? {
          trailerTypeId: toRefId(standard('type'), REF_KEYS.TRAILER_TYPE, getRefId) ?? null,
          typeSubtypeId: standard('typeSubtype') && !PLACEHOLDER.has(standard('type'))
            ? toRefId(standard('typeSubtype'), `TRAILER_TYPE_SUBTYPE_${normaliseToKey(standard('type'))}`, getRefId) ?? null
            : null,
          exteriorMaterialId: toRefId(standard('exterior'), REF_KEYS.TRAILER_EXTERIOR, getRefId) ?? null,
          lengthFt: toRefId(standard('length'), REF_KEYS.TRAILER_LENGTH, getRefId),
          heightIn: toRefId(additional('height'), REF_KEYS.TRAILER_HEIGHT, getRefId),
          widthIn: toRefId(additional('width'), REF_KEYS.TRAILER_WIDTH, getRefId),
          suspensionId: toRefId(additional('suspension'), REF_KEYS.TRAILER_SUSPENSION, getRefId) ?? null,
          interiorLiningId: toRefId(additional('interiorLining'), REF_KEYS.TRAILER_INTERIOR_LINING, getRefId) ?? null,
          rearDoorTypeId: toRefId(additional('rearDoorType'), REF_KEYS.TRAILER_REAR_DOOR_TYPE, getRefId) ?? null,
          gawrGvw: toRefId(additional('gawrGvv'), REF_KEYS.TRAILER_GAWR, getRefId),
          totalAxleCapacity: toOptionalInt(additional('totalAxleCapacity')),
          tireSize: additional('tireSize') || null,
          frontTireWearPct: toOptionalInt(condition('tiresFWearRmg')),
          rearTireWearPct: toOptionalInt(condition('tiresRWearRmg')),
          abs: toBool(additional('abs')),
          condLiningId: toRefId(condition('interiorLining'), REF_KEYS.TRAILER_VEHICLE_CONDITON, getRefId) ?? null,
          condBodyId: toRefId(condition('exteriorBody'), REF_KEYS.TRAILER_VEHICLE_CONDITON, getRefId) ?? null,
          condPaintId: toRefId(condition('exteriorPaint'), REF_KEYS.TRAILER_VEHICLE_CONDITON, getRefId) ?? null,
          condHydraulicsId: toRefId(condition('mechanicalHydraulics'), REF_KEYS.TRAILER_VEHICLE_CONDITON, getRefId) ?? null,
          condCoolingUnitId: toRefId(condition('mechanicalCoolingUnit'), REF_KEYS.TRAILER_VEHICLE_CONDITON, getRefId) ?? null,
          notes: condition('conditional_notes') || null,
        }
      : null,
    body,
    sectionImages,
  }
  
  return {
    claimReferenceNumber: basic('claimRefNo') ?? null,
    typeOfLossId: toRefId(basic('typeOfLoss'), REF_KEYS.TYPE_OF_LOSS, getRefId) ?? null,
    dateOfLoss: basic('dateOfLoss'),
    dateClaimReceived: basic('dateClaimReceived'),
    owner: basic('ownerId'),
    insured: basic('insured'),
    city: basic('contactCity'),
    state: toRefId(basic('contactState'), REF_KEYS.COUNTRY_STATE, getRefId) ?? null,
    valuationZipCode: basic('valuationZipCode'),
    vehicleTypeId: vehicleTypeId ?? null,
    vehicle,
  }
}

function buildBodyPayload(formValues: FormValues, getRefId?: RefIdLookup, attrCache?: AttrCache): ClaimVehicleBody[] {
  const standard = (id: string) => field(formValues, 'standard-equipment', id)
  const bodySection = formValues['body-information'] ?? {}
  if (!Object.keys(bodySection).length) return []

  const vehicleType = formValues['basic-information']?.['vehicleType'] ?? ''
  const vehicleKind = vehicleType === 'Truck/Tractor' ? 'truck'
    : vehicleType === 'Trailer' ? 'trailer' : ''
  const bodyUse = standard('use')
  const bodyType = standard('type')
  const bodyUseSubtype = standard('useSubtype')
  const bodyTypeSubtype = standard('typeSubtype')
  const useTypeId = vehicleKind === 'truck'
    ? toRefId(bodyUse, REF_KEYS.TRUCK_USE, getRefId) ?? null
    : vehicleKind === 'trailer' ? toRefId(bodyType, REF_KEYS.TRAILER_TYPE, getRefId) ?? null
    : null

  // Body category includes the Subtype suffix when one is applicable/selected — must
  // match the backend's TRUCK_BODY_<USE>_<SUBTYPE> / TRAILER_BODY_<TYPE>_<SUBTYPE> convention.
  const truckBodyPrefix = bodyUse && !PLACEHOLDER.has(bodyUse)
    ? `TRUCK_BODY_${normaliseToKey(bodyUse)}${bodyUseSubtype && !PLACEHOLDER.has(bodyUseSubtype) ? `_${normaliseToKey(bodyUseSubtype)}` : ''}`
    : null
  const trailerBodyPrefix = bodyType && !PLACEHOLDER.has(bodyType)
    ? `TRAILER_BODY_${normaliseToKey(bodyType)}${bodyTypeSubtype && !PLACEHOLDER.has(bodyTypeSubtype) ? `_${normaliseToKey(bodyTypeSubtype)}` : ''}`
    : null

  const attrDefs = vehicleKind === 'truck' && truckBodyPrefix
    ? attrCache?.[truckBodyPrefix]
    : vehicleKind === 'trailer' && trailerBodyPrefix
      ? attrCache?.[trailerBodyPrefix]
      : undefined

  const options: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(bodySection)) {
    if (key === '_photoUrls') continue
    if (!val || PLACEHOLDER.has(val)) continue

    if (getRefId) {
      let derivedCategory: string | null = null
      if (vehicleKind === 'truck' && truckBodyPrefix) {
        derivedCategory = `${truckBodyPrefix}_${normaliseToKey(key)}`
      } else if (vehicleKind === 'trailer' && trailerBodyPrefix) {
        derivedCategory = `${trailerBodyPrefix}_${normaliseToKey(key)}`
      }
      const category = resolveBodyFieldCategory(key, derivedCategory, attrDefs)
      if (category) {
        const id = getRefId(category, val)
        if (id !== undefined) {
          options[key] = id
          continue
        }
        // No ref ID found — field is plain text/number, fall through to store raw value
      }
    }
    options[key] = val
  }

  return [{
    typeId: useTypeId ?? null,
    options: Object.keys(options).length ? options : null,
  }]
}

const IMAGE_SOURCE_BY_BLOB_SECTION: Record<string, ClaimSectionImage['imageSource']> = {
  ai: 'ai',
  ocr: 'ocr',
  'form-recognizer': 'form-recognizer',
}

// FormRecognizer.tsx uploads scan-report PDFs under sectionId 'form-recognizer', which has no
// entry in the local SECTIONS list — ref_master already has its own row for it
// (category SECTIONS, code 'form-recognizer', label 'Form Recognizer'), so look it up directly.
const SECTION_NAME_OVERRIDES: Record<string, string> = {
  'form-recognizer': 'Form Recognizer',
}

function buildSectionImagesPayload(photos: PhotoItem[], getRefId?: RefIdLookup): ClaimSectionImage[] {
  return photos
    .filter((p) => p.serverName)
    .reduce<ClaimSectionImage[]>((acc, p) => {
      const sectionName = SECTION_NAME_OVERRIDES[p.sectionId] ?? SECTION_ID_TO_NAME[p.sectionId]
      const sectionId = sectionName ? getRefId?.(REF_KEYS.SECTION, sectionName) : undefined
      if (sectionId !== undefined) {
        const imageSource = p.blobSection ? IMAGE_SOURCE_BY_BLOB_SECTION[p.blobSection] : undefined
        acc.push({ sectionId, sectionName, imageName: p.serverName!, ...(imageSource ? { imageSource } : {}) })
      }
      return acc
    }, [])
}

// ── ClaimGetResponse → FormValues ────────────────────────────

export type RefLabelLookup = (category: string, id: number) => string | undefined

export function claimResponseToFormValues(data: ClaimGetResponse, getRefLabel?: RefLabelLookup, attrCache?: AttrCache): FormValues {
  const vehicle = data.vehicle

  /** Resolve a numeric ID to its display label, falling back to the ID string. */
  const toLabel = (id: number | null | undefined, category: string): string => {
    if (id === undefined || id === null) return ''
    return getRefLabel?.(category, id) ?? toStr(id)
  }

  const vehicleTypeLabel = toLabel(data.vehicleTypeId, REF_KEYS.VEHICLE_TYPE)
  const vehicleKind = vehicleTypeLabel === 'Truck/Tractor' ? 'truck'
    : vehicleTypeLabel === 'Trailer' ? 'trailer' : ''

  const basicInfo: Record<string, string> = {
    claimRefNo: data.claimReferenceNumber ?? '',
    typeOfLoss: toLabel(data.typeOfLossId, REF_KEYS.TYPE_OF_LOSS),
    dateOfLoss: data.dateOfLoss ?? '',
    dateClaimReceived: data.dateClaimReceived ?? '',
    ownerId: data.owner ?? '',
    insured: data.insured ?? '',
    contactCity: data.city ?? '',
    contactState: data.state ? toLabel(data.state, REF_KEYS.COUNTRY_STATE) : '',
    valuationZipCode: data.valuationZipCode ?? '',
    vehicleType: vehicleTypeLabel,
  }

  const makeCategory = vehicleKind === 'truck' ? REF_KEYS.TRUCK_MAKE
    : vehicleKind === 'trailer' ? REF_KEYS.TRAILER_MAKE : ''
  const makeLabel = toLabel(vehicle?.makeId, makeCategory)
  const modelCategory =
    makeLabel && !PLACEHOLDER.has(makeLabel)
      ? makeLabel.toLowerCase() === 'not in list'
        ? REF_KEYS.NOT_IN_LIST_MODEL
        : vehicleKind === 'truck'
          ? `TRUCK_MODEL_${normaliseToKey(makeLabel)}`
          : vehicleKind === 'trailer' ? `TRAILER_MODEL_${normaliseToKey(makeLabel)}` : ''
      : ''

  const transMfgLabel = vehicle.truck ? toLabel(vehicle.truck.transMfg, REF_KEYS.TRUCK_TRANS_MFG) : ''
  const transSpeedCategory =
    transMfgLabel && !PLACEHOLDER.has(transMfgLabel)
      ? transMfgLabel.toLowerCase() === 'not in list'
        ? REF_KEYS.NOT_IN_LIST_MODEL
        : `TRUCK_TRANS_SPEED_${normaliseToKey(transMfgLabel)}`
      : ''

  const standardEquipment: Record<string, string> = {
    vin: vehicle?.vin ?? '',
    year: toStr(vehicle?.year),
    make: makeLabel,
    model: modelCategory ? toLabel(vehicle?.modelId, modelCategory) : toStr(vehicle?.modelId),
    noOfAxles: toStr(vehicle?.numAxles),
    branded: toStr(vehicle?.brandedSalvageTitle)
  }

  if (vehicle.truck) {
    standardEquipment['use'] = toLabel(vehicle.truck.useTypeId, REF_KEYS.TRUCK_USE)
    standardEquipment['useSubtype'] = standardEquipment['use'] && !PLACEHOLDER.has(standardEquipment['use'])
      ? toLabel(vehicle.truck.useSubtypeId, `TRUCK_USE_SUBTYPE_${normaliseToKey(standardEquipment['use'])}`)
      : ''
    standardEquipment['gvw'] = toLabel(vehicle.truck.gvwrId, REF_KEYS.TRUCK_GVWR)
    standardEquipment['style'] = toLabel(vehicle.truck.styleId, REF_KEYS.TRUCK_STYLE)
    standardEquipment['truck_type'] = toLabel(vehicle.truck.typeId, REF_KEYS.TRUCK_TYPE)
    standardEquipment['engineMfg'] = toLabel(vehicle.truck.engineMfg, REF_KEYS.TRUCK_ENGINE_MFG)
    standardEquipment['engineModel'] = vehicle.truck.engineModel ?? ''
    standardEquipment['engineHP'] = toLabel(vehicle.truck.engineHp, REF_KEYS.TRUCK_ENGINE_HP)
    standardEquipment['transMfg'] = transMfgLabel
    standardEquipment['transModel'] = vehicle.truck.transModel ?? ''
    standardEquipment['noOfSpeeds'] = transSpeedCategory ? toLabel(vehicle.truck.numSpeeds, transSpeedCategory) : toStr(vehicle.truck.numSpeeds)
    standardEquipment['noOfDriveAxles'] = toStr(vehicle.truck.numDriveAxles)
    standardEquipment['noOfTagAxles'] = toStr(vehicle.truck.numTagAxles)
    standardEquipment['frontAxleRating'] = toLabel(vehicle.truck.frontAxleRatingLbs, REF_KEYS.TRUCK_FRONT_AXLE_RATING)
    standardEquipment['rearAxleRating'] = toLabel(vehicle.truck.rearAxleRatingLbs, REF_KEYS.TRUCK_REAR_AXLE_RATING)
    standardEquipment['odometer'] = toStr(vehicle.truck.odometer)
  } else if (vehicle.trailer) {
    standardEquipment['type'] = toLabel(vehicle.trailer.trailerTypeId, REF_KEYS.TRAILER_TYPE)
    standardEquipment['typeSubtype'] = standardEquipment['type'] && !PLACEHOLDER.has(standardEquipment['type'])
      ? toLabel(vehicle.trailer.typeSubtypeId, `TRAILER_TYPE_SUBTYPE_${normaliseToKey(standardEquipment['type'])}`)
      : ''
    standardEquipment['exterior'] = toLabel(vehicle.trailer.exteriorMaterialId, REF_KEYS.TRAILER_EXTERIOR)
    standardEquipment['length'] = toLabel(vehicle.trailer.lengthFt, REF_KEYS.TRAILER_LENGTH)
  }

  const additionalOptions: Record<string, string> = {}
  if (vehicle.truck) {
    additionalOptions['convSleeperSize'] = toLabel(vehicle.truck.sleeperConvSizeId, REF_KEYS.TRUCK_SLEEPER_SIZE)
    additionalOptions['coeSize'] = toLabel(vehicle.truck.sleeperCoeSizeId, REF_KEYS.TRUCK_COE_SIZE)
    additionalOptions['apu'] = toLabel(vehicle.truck.apuId, REF_KEYS.TRUCK_APU)
    additionalOptions['trimLevel'] = toLabel(vehicle.truck.trimLevelId, REF_KEYS.TRUCK_TRIM_LEVEL)
    additionalOptions['suspension'] = toLabel(vehicle.truck.suspensionId, REF_KEYS.TRUCK_SUSPENSION)
    additionalOptions['fifthWheelType'] = toLabel(vehicle.truck.fifthWheelTypeId, REF_KEYS.TRUCk_FIFTH_WHEEL_TYPE)
    additionalOptions['hitchType'] = toLabel(vehicle.truck.hitchTypeId, REF_KEYS.TRUCK_HITCH_TYPE)
    additionalOptions['frontWheelType'] = toLabel(vehicle.truck.frontWheelTypeId, REF_KEYS.TRUCK_WHEEL_TYPE)
    additionalOptions['osRearWheelType'] = toLabel(vehicle.osWheelTypeId, REF_KEYS.TRUCK_WHEEL_TYPE)
    additionalOptions['isRearWheelType'] = toLabel(vehicle.isWheelTypeId, REF_KEYS.TRUCK_WHEEL_TYPE)
    additionalOptions['frontTireSize'] = vehicle.truck.frontTireSize ?? ''
    additionalOptions['rearTireSize'] = vehicle.truck.rearTireSize ?? ''
    additionalOptions['liftGateCapacity'] = toLabel(vehicle.truck.liftGateCapacity, REF_KEYS.TRUCK_LIFT_GATE_CAPACITY)
    additionalOptions['gps'] = toStr(vehicle.truck.gps)
    additionalOptions['airRideCab'] = toStr(vehicle.truck.airRideCab)
    additionalOptions['airConditioning'] = toStr(vehicle.truck.airConditioning)
    additionalOptions['pto'] = toStr(vehicle.truck.pto)
    additionalOptions['wetLineKit'] = toStr(vehicle.truck.wetLineKit)
    additionalOptions['airBrakes'] = toStr(vehicle.truck.airBrakes)
    additionalOptions['engineBrake'] = toStr(vehicle.truck.engineBrake)
    additionalOptions['sideFairing'] = toStr(vehicle.truck.sideFairing)
    additionalOptions['windDeflector'] = toStr(vehicle.truck.windDeflector)
    additionalOptions['tankFairings'] = toStr(vehicle.truck.tankFairings)
    additionalOptions['fullAerodynamics'] = toStr(vehicle.truck.fullAerodynamics)
  } else if (vehicle.trailer) {
    additionalOptions['height'] = toLabel(vehicle.trailer.heightIn, REF_KEYS.TRAILER_HEIGHT)
    additionalOptions['width'] = toLabel(vehicle.trailer.widthIn, REF_KEYS.TRAILER_WIDTH)
    additionalOptions['suspension'] = toLabel(vehicle.trailer.suspensionId, REF_KEYS.TRAILER_SUSPENSION)
    additionalOptions['gawrGvv'] = toLabel(vehicle.trailer.gawrGvw, REF_KEYS.TRAILER_GAWR)
    additionalOptions['totalAxleCapacity'] = toStr(vehicle.trailer.totalAxleCapacity)
    additionalOptions['abs'] = toStr(vehicle.trailer.abs)
    additionalOptions['interiorLining'] = toLabel(vehicle.trailer.interiorLiningId, REF_KEYS.TRAILER_INTERIOR_LINING)
    additionalOptions['rearDoorType'] = toLabel(vehicle.trailer.rearDoorTypeId, REF_KEYS.TRAILER_REAR_DOOR_TYPE)
    additionalOptions['osWheelType'] = toLabel(vehicle.osWheelTypeId, REF_KEYS.TRAILER_OS_WHEEL_TYPE)
    additionalOptions['isWheelType'] = toLabel(vehicle.isWheelTypeId, REF_KEYS.TRAILER_IS_WHEEL_TYPE)
    additionalOptions['tireSize'] = vehicle.trailer.tireSize ?? ''
  }

  const vehicleCondition: Record<string, string> = { conditional_notes: vehicle.notes ?? '' }
  if (vehicle.truck) {
    vehicleCondition['interior'] = toLabel(vehicle.truck.condInteriorId, REF_KEYS.TRUCK_VEHICLE_CONDITON)
    vehicleCondition['body'] = toLabel(vehicle.condBodyId, REF_KEYS.TRUCK_VEHICLE_CONDITON)
    vehicleCondition['interiorGlass'] = toLabel(vehicle.truck.condGlassId, REF_KEYS.TRUCK_VEHICLE_CONDITON)
    vehicleCondition['interiorPaint'] = toLabel(vehicle.condPaintId, REF_KEYS.TRUCK_VEHICLE_CONDITON)
    vehicleCondition['drivetrainEngine'] = toLabel(vehicle.truck.condEngineId, REF_KEYS.TRUCK_VEHICLE_CONDITON)
    vehicleCondition['drivetrainTransmission'] = toLabel(vehicle.truck.condTransId, REF_KEYS.TRUCK_VEHICLE_CONDITON)
    vehicleCondition['tiresFWearRmg'] = toStr(vehicle.frontTireWearPct)
    vehicleCondition['tiresRWearRmg'] = toStr(vehicle.rearTireWearPct)
  } else if (vehicle.trailer) {
    vehicleCondition['interiorLining'] = toLabel(vehicle.trailer.condLiningId, REF_KEYS.TRAILER_VEHICLE_CONDITON)
    vehicleCondition['exteriorBody'] = toLabel(vehicle.condBodyId, REF_KEYS.TRAILER_VEHICLE_CONDITON)
    vehicleCondition['exteriorPaint'] = toLabel(vehicle.condPaintId, REF_KEYS.TRAILER_VEHICLE_CONDITON)
    vehicleCondition['mechanicalHydraulics'] = toLabel(vehicle.trailer.condHydraulicsId, REF_KEYS.TRAILER_VEHICLE_CONDITON)
    vehicleCondition['mechanicalCoolingUnit'] = toLabel(vehicle.trailer.condCoolingUnitId, REF_KEYS.TRAILER_VEHICLE_CONDITON)
    vehicleCondition['tiresFWearRmg'] = toStr(vehicle.frontTireWearPct)
    vehicleCondition['tiresRWearRmg'] = toStr(vehicle.rearTireWearPct)
  }

  const bodyInformation: Record<string, string> = {}
  if (vehicle?.body?.length) {
    const firstBody = vehicle.body[0]
    if (firstBody.options && typeof firstBody.options === 'object') {
      const bodyUseLabel = standardEquipment['use'] ?? ''
      const bodyTypeLabel = standardEquipment['type'] ?? ''
      const bodyUseSubtypeLabel = standardEquipment['useSubtype'] ?? ''
      const bodyTypeSubtypeLabel = standardEquipment['typeSubtype'] ?? ''
      // Body category includes the Subtype suffix when one was restored — must match the
      // same TRUCK_BODY_<USE>_<SUBTYPE> / TRAILER_BODY_<TYPE>_<SUBTYPE> convention used on save.
      const truckBodyPrefix = bodyUseLabel && !PLACEHOLDER.has(bodyUseLabel)
        ? `TRUCK_BODY_${normaliseToKey(bodyUseLabel)}${bodyUseSubtypeLabel && !PLACEHOLDER.has(bodyUseSubtypeLabel) ? `_${normaliseToKey(bodyUseSubtypeLabel)}` : ''}`
        : null
      const trailerBodyPrefix = bodyTypeLabel && !PLACEHOLDER.has(bodyTypeLabel)
        ? `TRAILER_BODY_${normaliseToKey(bodyTypeLabel)}${bodyTypeSubtypeLabel && !PLACEHOLDER.has(bodyTypeSubtypeLabel) ? `_${normaliseToKey(bodyTypeSubtypeLabel)}` : ''}`
        : null
      const attrDefs = vehicleKind === 'truck' && truckBodyPrefix
        ? attrCache?.[truckBodyPrefix]
        : vehicleKind === 'trailer' && trailerBodyPrefix
          ? attrCache?.[trailerBodyPrefix]
          : undefined
      for (const [key, val] of Object.entries(firstBody.options)) {
        if (key === '_photoUrls') continue
        if (typeof val === 'number' && getRefLabel) {
          let derivedCategory: string | null = null
          if (vehicleKind === 'truck' && truckBodyPrefix) {
            derivedCategory = `${truckBodyPrefix}_${normaliseToKey(key)}`
          } else if (vehicleKind === 'trailer' && trailerBodyPrefix) {
            derivedCategory = `${trailerBodyPrefix}_${normaliseToKey(key)}`
          }
          const category = resolveBodyFieldCategory(key, derivedCategory, attrDefs)
          if (category) {
            bodyInformation[key] = getRefLabel(category, val) ?? toStr(val)
            continue
          }
        }
        bodyInformation[key] = toStr(val as string | number | boolean | null)
      }
    }
  }

  return {
    'basic-information': basicInfo,
    'standard-equipment': standardEquipment,
    'additional-options': additionalOptions,
    'vehicle-condition': vehicleCondition,
    'body-information': bodyInformation,
  }
}
